/**
 * A toast ONLY when something was refused — ROADMAP follow-up to 4.15.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY SUCCESS IS SILENT
 *
 * Every placement used to raise "Accepted by the broker". It was the wrong
 * promise twice over: accepted is not filled, and the drawer that opens on the
 * same click already shows the order with its real state. Two answers to one
 * question, the louder one less true, and a trader working a ladder collects a
 * toast per click until the stack hides the one that matters.
 *
 * So the screen speaks only to interrupt. Everything that goes right is read
 * from the book; everything that goes wrong is said out loud.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A WATCHER AND NOT ANOTHER `await`
 *
 * A refusal has three origins and only one of them is answerable where the
 * order was sent:
 *
 *   our boundary   `placeOrder` returns !ok      — the caller already says so
 *   our engine     an RMS gate refuses           — arrives as an event, later
 *   the broker     or the exchange behind it     — arrives as an event, later
 *
 * The last two land on the execution stream some time after the click handler
 * has returned, and they land PER LEG: a basket can be refused for a different
 * reason on each leg, so "one of them failed" is not an answer to which or why.
 *
 *   click ──▶ placeOrder ──▶ ack !ok ──────────────▶ caller toasts (sync)
 *                 │
 *                 └─▶ store ──▶ engine/broker event ──▶ THIS toasts (async)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DAY'S HISTORY IS NOT NEWS — AND THIS IS THE HARD PART
 *
 * `hydrate` loads every order of the day on open, so a refresh at 15:00 shows
 * 107 rows of which 85 were refused hours ago. None of them may speak.
 *
 * The first version of this gated on "have I seen this row before", which was
 * wrong in a way that only shows up on the SECOND store change: hydrate lands
 * as one rebuild, every row is unseen, every row is skipped and recorded — and
 * then opening the drawer hydrates again, at which point all 85 are "seen" and
 * all 85 toast at once. Reported 2026-10-27.
 *
 * So first sight does not mean "skip this time". It decides, PERMANENTLY,
 * whether the row can ever speak: a row first seen in a settled state is
 * history and is never spoken about again, however many times it is re-put.
 * A row first seen still in flight is live, and everything that happens to it
 * from then on is news — which covers every order placed from this tab,
 * because `placeOrder` records it as SUBMITTING before the ack.
 *
 * One hole that leaves, closed explicitly: a PARTIAL row is not settled, so it
 * is live, but it may ALREADY carry a refused leg from before we arrived.
 * Those legs are marked spoken-for at first sight so only later ones speak.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect } from 'react'
import { notifyFail } from '@/components/Toast'
import { isSettled, orderStore, type TrackedIntent } from '@/store/orders'

type LegState = TrackedIntent['legs'][number]['state']

/** Terminal states that mean the size did not go on. */
const isRefusal = (state: LegState | TrackedIntent['state']): boolean =>
  state === 'REJECTED' || state === 'REFUSED'

export interface RefusalNotice {
  /** Stable per refusal, so one cannot be raised twice. */
  key: string
  title: string
  detail?: string
}

/**
 * Reads successive order-store snapshots and reports refusals worth saying.
 *
 * Stateful across calls and deliberately not a hook: the rule it enforces is
 * about the SEQUENCE of snapshots, which is the part that was wrong before and
 * the part worth testing without a DOM.
 */
export function createRefusalReporter(): { read: (snapshot: readonly TrackedIntent[]) => RefusalNotice[] } {
  /** clientRequestId -> may this row ever speak. Written once, at first sight. */
  const live = new Map<string, boolean>()
  /** Everything already spoken for, including the pre-existing legs of a live row. */
  const told = new Set<string>()

  const legKey = (id: string, leg: TrackedIntent['legs'][number]): string =>
    `${id}:${leg.orderRowId ?? leg.token}`

  return {
    read(snapshot) {
      const out: RefusalNotice[] = []
      for (const intent of snapshot) {
        const id = intent.clientRequestId

        if (!live.has(id)) {
          const inFlight = !isSettled(intent.state)
          live.set(id, inFlight)
          // Already refused when we arrived: history inside a live row.
          if (inFlight) for (const leg of intent.legs) if (isRefusal(leg.state)) told.add(legKey(id, leg))
        }
        if (!live.get(id)) continue

        for (const leg of intent.legs) {
          if (!isRefusal(leg.state)) continue
          const key = legKey(id, leg)
          if (told.has(key)) continue
          told.add(key)
          out.push({
            key,
            title: `${leg.side} ${leg.symbol ?? leg.token} ${leg.state === 'REJECTED' ? 'rejected' : 'refused'}`,
            ...(leg.reason ?? intent.message ? { detail: leg.reason ?? intent.message ?? undefined } : {}),
          })
        }

        /*
         * The whole order, when no leg can carry the reason: a gate refuses
         * before any leg has a state of its own. An intent REFUSED by the HTTP
         * ack carries no `refusedGate` and was already reported by the caller,
         * which is what keeps the two paths from both speaking.
         */
        const key = `${id}:intent`
        const gated = intent.state === 'REFUSED' && intent.refusedGate !== null
        const rejected = intent.state === 'REJECTED'
        // Only when NO leg spoke. A gate that refuses a basket marks the legs
        // too, and "the order was refused" beside "this leg was refused" is
        // one event reported twice.
        const legsSpoke = intent.legs.some((l) => isRefusal(l.state))
        if ((gated || rejected) && !legsSpoke && !told.has(key)) {
          told.add(key)
          out.push({
            key,
            title: `${intent.label} refused`,
            ...(intent.message ?? intent.refusedGate ? { detail: intent.message ?? intent.refusedGate ?? undefined } : {}),
          })
        }
      }
      return out
    },
  }
}

export function useOrderFailureToasts(): void {
  useEffect(() => {
    const reporter = createRefusalReporter()
    // The mount pass only RECORDS — by construction it can return nothing,
    // because a settled row is marked silent and a live row's existing
    // refusals are marked spoken-for in the same pass.
    reporter.read(orderStore.getSnapshot())
    return orderStore.subscribe(() => {
      for (const notice of reporter.read(orderStore.getSnapshot())) notifyFail(notice.title, notice.detail)
    })
  }, [])
}
